/**
 * @aigntx/ai-gateway — shared AI configuration contract.
 *
 * The runtime AI gateway (which actually calls the models, meters usage, and
 * enforces quotas) lives in the Python API. This package is the *shared shape*
 * used by the web control-plane forms and the API config so both agree on what
 * a tenant's AI configuration looks like. Providers are pluggable: the tenant
 * chooses one; swapping later is a config change, not a code change.
 */

export type AiProviderId = "claude-worker" | "anthropic-api" | "openai" | "custom";

export interface AiQuota {
  /** Hard ceiling on output tokens per day (per tenant). */
  tokensPerDay?: number;
  requestsPerMin?: number;
}

export interface AiConfig {
  provider: AiProviderId;
  model: string;
  quota?: AiQuota;
  /** Base URL for `custom` / self-hosted providers. */
  baseUrl?: string;
}

export interface AiProviderInfo {
  id: AiProviderId;
  label: string;
  /** Whether this provider bills against the shared agentic worker subscription. */
  subscription: boolean;
  models: string[];
}

/** Catalogue surfaced in the control-plane provider picker. */
export const AI_PROVIDERS: readonly AiProviderInfo[] = [
  {
    id: "claude-worker",
    label: "Claude agentic worker (subscription)",
    subscription: true,
    models: ["claude-opus-4-8", "claude-sonnet-5", "claude-haiku-4-5-20251001", "claude-fable-5"],
  },
  {
    id: "anthropic-api",
    label: "Anthropic API (metered key)",
    subscription: false,
    models: ["claude-opus-4-8", "claude-sonnet-5", "claude-haiku-4-5-20251001"],
  },
  { id: "openai", label: "OpenAI", subscription: false, models: ["gpt-4o", "gpt-4o-mini"] },
  { id: "custom", label: "Custom / self-hosted", subscription: false, models: [] },
];

export const DEFAULT_AI: AiConfig = {
  provider: "claude-worker",
  model: "claude-opus-4-8",
  quota: { tokensPerDay: 5_000_000 },
};

export function providerInfo(id: AiProviderId): AiProviderInfo | undefined {
  return AI_PROVIDERS.find((p) => p.id === id);
}

/** True when the model is valid for the provider (empty model list = anything). */
export function isModelAllowed(cfg: AiConfig): boolean {
  const info = providerInfo(cfg.provider);
  if (!info) return false;
  return info.models.length === 0 || info.models.includes(cfg.model);
}
